import React, { useEffect, useState } from "react";
import Drawer from "../shared/Drawer";

const VARIABLES = [
  "{FIRST_NAME}",
  "{LAST_NAME}",
  "{COMPANY}",
  "{POSITION}",
  "{LOCATION}",
  "{INDUSTRY}",
];

export default function SendMessageDrawer({ open, step, onClose, onSave }) {
  const [content, setContent] = useState("");
  const [fallback, setFallback] = useState("");
  const [externalReference, setExternalReference] = useState("");
  const [variantId, setVariantId] = useState("A");
  const [variants, setVariants] = useState([{ id: "A", content: "", weight: 100 }]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [delay, setDelay] = useState(0);
  const [delayUnit, setDelayUnit] = useState("hours");

  useEffect(() => {
    if (!step) return;
    const initialVariants =
      step.messageVariants?.length > 0
        ? step.messageVariants
        : [{ id: "A", content: step.content || "", weight: 100 }];
    setVariants(initialVariants);
    setVariantId(initialVariants[0].id);
    setContent(initialVariants[0].content || step.content || "");
    setFallback(step.fallbackContent || step.fallbackMessage || "");
    setExternalReference(step.externalReference || "");
    setDelay(step.delay ?? 0);
    setDelayUnit(step.delayUnit || "hours");
  }, [step]);

  const charsLeft = 8000 - content.length;

  const insertVariable = (variable) => {
    setContent((prev) => `${prev}${variable}`);
  };

  const switchVariant = (id) => {
    setVariants((prev) =>
      prev.map((v) => (v.id === variantId ? { ...v, content } : v)),
    );
    const next = variants.find((v) => v.id === id);
    setVariantId(id);
    setContent(next?.content || "");
  };

  const addVariant = () => {
    const nextId = String.fromCharCode(65 + variants.length);
    const updated = variants.map((v) => (v.id === variantId ? { ...v, content } : v));
    updated.push({ id: nextId, content: "", weight: Math.floor(100 / (variants.length + 1)) });
    setVariants(updated);
    setVariantId(nextId);
    setContent("");
  };

  const handleSave = () => {
    const finalVariants = variants.map((v) =>
      v.id === variantId ? { ...v, content } : v,
    );
    onSave?.({
      ...step,
      content: finalVariants[0]?.content || content,
      messageVariants: finalVariants,
      fallbackContent: fallback,
      fallbackMessage: fallback,
      externalReference,
      delay: Number(delay) || 0,
      delayUnit,
      label: "Send message",
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Send Message"
      widthClass="max-w-lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">
            Dismiss
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm"
          >
            Save
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <select
            value={variantId}
            onChange={(e) => switchVariant(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                Version {v.id}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={addVariant}
            className="w-8 h-8 rounded-lg bg-blue-500 text-white text-lg leading-none"
          >
            +
          </button>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={8}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
          placeholder="Write your message…"
        />
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) insertVariable(e.target.value);
                e.target.value = "";
              }}
              className="border rounded-lg px-2 py-1"
            >
              <option value="">Add variables</option>
              {VARIABLES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <span>{charsLeft} characters left</span>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Fallback message</label>
          <textarea
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            rows={3}
            className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
            placeholder="Used when variables are missing"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Delay</label>
            <input
              type="number"
              min={0}
              value={delay}
              onChange={(e) => setDelay(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Unit</label>
            <select
              value={delayUnit}
              onChange={(e) => setDelayUnit(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-sm text-blue-600"
        >
          {showAdvanced ? "Hide" : "Advanced"} settings
        </button>
        {showAdvanced ? (
          <div>
            <label className="text-sm font-medium text-gray-700">External reference</label>
            <input
              value={externalReference}
              onChange={(e) => setExternalReference(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Name your step"
            />
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
